"use client"

import { useState, useEffect, useRef } from "react"
import { useNavigate } from "react-router-dom"
import Layout from "../components/Layout"
import Card from "../components/Card"
import Button from "../components/Button"
import Alert from "../components/Alert"
import { Search, Filter, Download, Edit, Trash2, Clock } from "react-feather"
import MedicineInfoModal from "../components/MedicineInfoModal"
import MedicineHistoryModal from "../components/MedicineHistoryModal"



const Medicine = () => {
  const [medicines, setMedicines] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [searchTerm, setSearchTerm] = useState("")
  const [filteredMedicines, setFilteredMedicines] = useState([])
  const [typeFilter, setTypeFilter] = useState("")
  const [availabilityFilter, setAvailabilityFilter] = useState("")
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [alert, setAlert] = useState(null)
  const [selectedMedicine, setSelectedMedicine] = useState(null)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [editModalOpen, setEditModalOpen] = useState(false)
  const [editMedicine, setEditMedicine] = useState(null)
  const [actionLoading, setActionLoading] = useState(false)
  const [historyModalOpen, setHistoryModalOpen] = useState(false)
  const [selectedMedicineForHistory, setSelectedMedicineForHistory] = useState(null)
  const API_BASE_URL = 'https://api.tenarx.net';
  const editFormRef = useRef()
  



const fetchMedicines = async (page = 1, type = "") => {
  try {
    setLoading(true);
    let token = localStorage.getItem("userToken");
    if (!token) {
      setAlert({
        type: "error",
        message: "You must be logged in to view medicines",
      });
      return;
    }
    const url = new URL(`${API_BASE_URL}/api/medicines/my`);
    url.searchParams.append("page", page);
    url.searchParams.append("limit", 10);
    if (type) url.searchParams.append("type", type);
    const response = await fetch(url.toString(), {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    });
    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.message || "Failed to fetch medicines");
    }
    setMedicines(data.data || []);
    setFilteredMedicines(data.data || []);
    setTotalPages(data.totalPages || 1);
  } catch (error) {
    setError(error.message);
    setAlert({
      type: "error",
      message: error.message,
    });
  } finally {
    setLoading(false);
  }
};

  useEffect(() => {
    fetchMedicines(currentPage, typeFilter)
    // eslint-disable-next-line
  }, [currentPage])

  // Client-side search, type, and availability filter
  useEffect(() => {
    let filtered = medicines
    if (searchTerm) {
      filtered = filtered.filter((m) =>
        m.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        m.content.toLowerCase().includes(searchTerm.toLowerCase())
      )
    }
    if (typeFilter) {
      filtered = filtered.filter((m) => m.type === typeFilter)
    }
    if (availabilityFilter) {
      filtered = filtered.filter((m) =>
        availabilityFilter === "available" ? m.isAvailable : !m.isAvailable
      )
    }
    setFilteredMedicines(filtered)
  }, [searchTerm, medicines, typeFilter, availabilityFilter])

  const handleSearch = () => {
    setCurrentPage(1)
    // No backend fetch, just filter client-side
  }

  const handleTypeFilterChange = (e) => {
    setTypeFilter(e.target.value)
    setCurrentPage(1)
  }

  const handleAvailabilityFilterChange = (e) => {
    setAvailabilityFilter(e.target.value)
    setCurrentPage(1)
  }

  const handleExport = () => {
    // This would typically call an API endpoint that returns a CSV/Excel file
    setAlert({
      type: "info",
      message: "Export functionality would be implemented here",
    })
  }

  const handleMedicineClick = (medicine) => {
    setSelectedMedicine(medicine)
    setIsModalOpen(true)
  }

  const handleCloseModal = () => {
    setIsModalOpen(false)
    setSelectedMedicine(null)
  }

  const handleToggleAvailability = async (medicine) => {
    setActionLoading(true)
    setAlert(null)
    try {
      const token = localStorage.getItem("userToken")
      const res = await fetch(`${API_BASE_URL}/api/medicines/toggle-status/${medicine._id}`, {
        method: "PATCH",
        headers: { Authorization: `Bearer ${token}` },
      })
      const data = await res.json()
      if (res.ok && data.success) {
        fetchMedicines(currentPage, typeFilter)
      } else {
        setAlert({ type: "error", message: data.message || "Failed to toggle availability" })
      }
    } catch (err) {
      setAlert({ type: "error", message: "Failed to toggle availability" })
    } finally {
      setActionLoading(false)
    }
  }

  const handleDelete = async (medicine) => {
    if (!window.confirm("Are you sure you want to delete this medicine?")) return
    setActionLoading(true)
    setAlert(null)
    try {
      const token = localStorage.getItem("userToken")
      const res = await fetch(`${API_BASE_URL}/api/medicines/medicine/${medicine._id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      })
      const data = await res.json()
      if (res.ok && data.success) {
        fetchMedicines(currentPage, typeFilter)
      } else {
        setAlert({ type: "error", message: data.message || "Failed to delete medicine" })
      }
    } catch (err) {
      setAlert({ type: "error", message: "Failed to delete medicine" })
    } finally {
      setActionLoading(false)
    }
  }

  const handleEdit = (medicine) => {
    setEditMedicine(medicine)
    setEditModalOpen(true)
  }

  const handleViewHistory = (medicine) => {
    setSelectedMedicineForHistory(medicine)
    setHistoryModalOpen(true)
  }

  const handleEditSubmit = async (e) => {
    e.preventDefault()
    setActionLoading(true)
    setAlert(null)
    try {
      const token = localStorage.getItem("userToken")
      const form = editFormRef.current
      const updated = {
        name: form.name.value,
        content: form.content.value,
        measurementUnit: form.measurementUnit.value,
        country: form.country.value,
        type: form.type.value,
        expireDate: form.expireDate.value,
      }
      const res = await fetch(`${API_BASE_URL}/api/medicines/medicine/${editMedicine._id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(updated),
      })
      const data = await res.json()
      if (res.ok && data.success) {
        setEditModalOpen(false)
        setEditMedicine(null)
        fetchMedicines(currentPage, typeFilter)
      } else {
        setAlert({ type: "error", message: data.message || "Failed to update medicine" })
      }
    } catch (err) {
      setAlert({ type: "error", message: "Failed to update medicine" })
    } finally {
      setActionLoading(false)
    }
  }

  const columns = [
    {
      header: "No",
      accessor: "",
      render: (row, index) => (currentPage - 1) * 10 + index + 1,
      style: { width: "50px" },
    },
    {
      header: "Name",
      accessor: "name",
    },
    {
      header: "Content",
      accessor: "content",
      render: (row) => `${row.content} ${row.measurementUnit}`, // merge content + measurement
    },
  
    {
      header: "Country",
      accessor: "country",
    },
    {
      header: "Type",
      accessor: "type",
      render: (row) => (
        <span className="px-2 py-1 text-xs font-semibold rounded-full capitalize bg-blue-100 text-blue-800">
          {row.type}
        </span>
      ),
    },
    {
      header: "Expire Date",
      accessor: "expireDate",
      render: (row) => new Date(row.expireDate).toLocaleDateString(),
    },
    {
      header: "Availability",
      accessor: "isAvailable",
      render: (row) => (
        <Button
          size="sm"
          style={{ minWidth: 90 }}
          variant={row.isAvailable ? "primary" : "danger"}
          onClick={(e) => { e.stopPropagation(); handleToggleAvailability(row) }}
          disabled={actionLoading}
        >
          {row.isAvailable ? "Available" : "Sold Out"}
        </Button>
      ),
    },
    {
      header: "Actions",
      accessor: "actions",
      render: (row) => (
        <div className="flex gap-2">
          <Button size="sm" onClick={(e) => { e.stopPropagation(); handleViewHistory(row) }}>
            <Clock className="w-4 h-4" />
          </Button>
          <Button size="sm" onClick={(e) => { e.stopPropagation(); handleEdit(row) }}>
            <Edit className="w-4 h-4" />
          </Button>
          <Button
  size="sm"
  className="bg-red-500 hover:bg-red-600 text-white"
  onClick={(e) => {
    e.stopPropagation();
    handleDelete(row);
  }}
  disabled={actionLoading}
>
  <Trash2 className="w-4 h-4" />
</Button>

        </div>
      ),
    },
  ]

  return (
    <Layout title="Medicines">
      {alert && (
        <Alert type={alert.type} dismissible onDismiss={() => setAlert(null)}>
          {alert.message}
        </Alert>
      )}

      <Card>
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative">
              <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                <Search className="w-4 h-4 text-gray-500" />
              </div>
              <input
                type="text"
                placeholder="Search medicines..."
                className="pl-10 pr-4 py-2 border rounded-lg w-full"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyPress={(e) => e.key === "Enter" && handleSearch()}
              />
            </div>

            <div className="flex items-center">
              <Filter className="w-4 h-4 text-gray-500 mr-2" />
              <select className="border rounded-lg px-4 py-2" value={typeFilter} onChange={handleTypeFilterChange}>
                <option value="">All Types</option>
               <option value="tablet">Tablet</option>
                <option value="capsule">Capsule</option>
                <option value="syrup">Syrup</option>
                <option value="injection">Injection</option>
                <option value="cream">Cream</option>
                <option value="ointment">Ointment</option>
                <option value="drops">Drops</option>
                <option value="powder">Powder</option>
                <option value="inhaler">Inhaler</option>
                <option value="patch">Patch</option>
                <option value="liquid">Liquid</option>
                <option value="lozenge">Lozenge</option>
                <option value="nasal spray">Nasal Spray</option>
              </select>
            </div>
            <div className="flex items-center">
              <span className="mr-2 text-gray-500 text-sm">Availability</span>
              <select className="border rounded-lg px-4 py-2" value={availabilityFilter} onChange={handleAvailabilityFilterChange}>
                <option value="">All</option>
                <option value="available">Available</option>
                <option value="soldout">Sold Out</option>
              </select>
            </div>
          </div>

          <div className="flex gap-2">
          </div>
        </div>

        <div className="relative overflow-x-auto rounded-lg border border-gray-200">
          <table className="w-full text-sm text-left text-gray-500">
            <thead className="text-xs text-gray-700 uppercase bg-gray-50">
              <tr>
                {columns.map((column, index) => (
                  <th key={index} className="px-6 py-3" style={column.style}>
                    {column.header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={columns.length} className="px-6 py-4 text-center">
                    <div className="flex justify-center items-center py-4">
                      <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-blue-500"></div>
                      <span className="ml-2">Loading...</span>
                    </div>
                  </td>
                </tr>
              ) : medicines.length === 0 ? (
                <tr>
                  <td colSpan={columns.length} className="px-6 py-4 text-center">
                    No medicines found
                  </td>
                </tr>
              ) : (
                filteredMedicines.map((row, rowIndex) => (
                  <tr
                    key={rowIndex}
                    className="bg-white border-b hover:bg-gray-50 cursor-pointer transition-colors duration-200"
                    onClick={() => handleMedicineClick(row)}
                  >
                    {columns.map((column, colIndex) => (
                      <td key={colIndex} className="px-6 py-4" style={column.style}>
                        {column.render ? column.render(row, rowIndex) : row[column.accessor]}
                      </td>
                    ))}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex justify-center mt-6">
            <nav className="inline-flex rounded-md shadow">
              <button
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className="px-3 py-1 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                Previous
              </button>
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum
                if (totalPages <= 5) {
                  pageNum = i + 1
                } else if (currentPage <= 3) {
                  pageNum = i + 1
                } else if (currentPage >= totalPages - 2) {
                  pageNum = totalPages - 4 + i
                } else {
                  pageNum = currentPage - 2 + i
                }
                return (
                  <button
                    key={pageNum}
                    onClick={() => setCurrentPage(pageNum)}
                    className={`px-3 py-1 border-t border-b border-gray-300 bg-white text-sm font-medium ${
                      currentPage === pageNum
                        ? "bg-blue-50 text-blue-600 border-blue-500"
                        : "text-gray-700 hover:bg-gray-50"
                    }`}
                  >
                    {pageNum}
                  </button>
                )
              })}
              <button
                onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages}
                className="px-3 py-1 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                Next
              </button>
            </nav>
          </div>
        )}
      </Card>
      <MedicineInfoModal medicine={selectedMedicine} isOpen={isModalOpen} onClose={handleCloseModal} />
      <MedicineHistoryModal 
        medicineId={selectedMedicineForHistory?._id} medicineName={selectedMedicineForHistory?.name} 
        isOpen={historyModalOpen} 
        onClose={() => { setHistoryModalOpen(false); setSelectedMedicineForHistory(null); }} 
      />
      {/* Edit Modal */}
      {editModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
          <div className="bg-white rounded-lg shadow-lg max-w-lg w-full p-6 relative mx-2">
            <button
              className="absolute top-2 right-2 text-gray-400 hover:text-gray-700 text-2xl font-bold focus:outline-none"
              onClick={() => { setEditModalOpen(false); setEditMedicine(null) }}
              aria-label="Close edit modal"
            >
              &times;
            </button>
            <h2 className="text-xl font-bold text-gray-900 mb-4">Edit Medicine</h2>
            <form ref={editFormRef} onSubmit={handleEditSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Name</label>
                <input name="name" defaultValue={editMedicine?.name} className="w-full px-3 py-2 border border-gray-300 rounded-md" required />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Content</label>
                <input name="content" defaultValue={editMedicine?.content} className="w-full px-3 py-2 border border-gray-300 rounded-md" required />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Measurement Unit</label>
                <input name="measurementUnit" defaultValue={editMedicine?.measurementUnit} className="w-full px-3 py-2 border border-gray-300 rounded-md" required />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Country</label>
                <input name="country" defaultValue={editMedicine?.country} className="w-full px-3 py-2 border border-gray-300 rounded-md" required />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Type</label>
                <input name="type" defaultValue={editMedicine?.type} className="w-full px-3 py-2 border border-gray-300 rounded-md" required />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Expire Date</label>
                <input name="expireDate" type="date" defaultValue={editMedicine?.expireDate?.slice(0, 10)} className="w-full px-3 py-2 border border-gray-300 rounded-md" required />
              </div>
              <Button type="submit" disabled={actionLoading}>{actionLoading ? "Saving..." : "Save Changes"}</Button>
            </form>
          </div>
        </div>
      )}
    </Layout>
  )
}

export default Medicine
